// Dev-only timing for the Analytics dashboard. Records milestones (ms since
// navigation start) and per-request latency of POST /metrics/playerAnalytics,
// then prints one console.table once the landing has settled. Compiled out of
// production builds via the NODE_ENV check; every export is a no-op there.

const enabled = process.env.NODE_ENV !== 'production'
	&& typeof window !== 'undefined'
	&& typeof performance !== 'undefined';

const milestones = new Map(); // name -> ms since navigation start
const requests = []; // { label, startedAt, duration, fromCache }
let reportTimer = null;
let reported = false;

function now() {
	return Math.round(performance.now());
}

/**
 * Record a milestone once; later calls with the same name are ignored so the
 * first KPI/leaderboard/etc. keep their real time.
 */
export function perfMark(name) {
	if (!enabled || milestones.has(name)) return;
	milestones.set(name, now());
	try {
		performance.mark(`analytics:${name}`);
	} catch (e) {
		// Older browsers without User Timing; the table still works.
	}
	scheduleReport();
}

/**
 * Start timing a request; returns a function to call when it settles.
 */
export function perfRequestStart(label) {
	if (!enabled) return () => {};
	const startedAt = now();
	try {
		performance.mark(`analytics:req:${label}:start`);
	} catch (e) { /* noop */ }
	return ({ fromCache = false, failed = false } = {}) => {
		const endedAt = now();
		requests.push({ label, startedAt, endedAt, duration: endedAt - startedAt, fromCache, failed });
		try {
			performance.measure(`analytics:req:${label}`, `analytics:req:${label}:start`);
		} catch (e) { /* noop */ }
		scheduleReport();
	};
}

/**
 * Forget everything (called when filters change and a new landing starts).
 */
export function perfReset() {
	if (!enabled) return;
	milestones.clear();
	requests.length = 0;
	reported = false;
	clearTimeout(reportTimer);
}

/**
 * Snapshot used by the console report and by harnesses via
 * window.__castrAnalyticsPerf().
 */
export function perfSnapshot() {
	return {
		milestones: Object.fromEntries(milestones),
		requests: requests.slice(),
	};
}

function scheduleReport() {
	if (!enabled || reported) return;
	clearTimeout(reportTimer);
	// Print when things have been quiet for a moment (all first-paint data in).
	reportTimer = setTimeout(() => {
		if (!milestones.has('landing-settled')) return;
		reported = true;
		const rows = Array.from(milestones.entries())
			.sort((a, b) => a[1] - b[1])
			.map(([milestone, ms]) => ({ milestone, 'ms since nav': ms }));
		// eslint-disable-next-line no-console
		console.groupCollapsed(`[analytics perf] first KPI ${milestones.get('first-kpi') ?? '-'}ms · leaderboard ${milestones.get('leaderboard') ?? '-'}ms · settled ${milestones.get('landing-settled') ?? '-'}ms`);
		// eslint-disable-next-line no-console
		console.table(rows);
		// eslint-disable-next-line no-console
		console.table(requests.map((r) => ({ request: r.label, 'started @ms': r.startedAt, 'took ms': r.duration, cache: r.fromCache ? 'hit' : '', failed: r.failed ? 'yes' : '' })));
		// eslint-disable-next-line no-console
		console.groupEnd();
	}, 1500);
}

if (enabled) {
	window.__castrAnalyticsPerf = perfSnapshot;
}
